import { Link } from "react-router-dom";
import { Clock, Users, Check, ArrowRight } from "lucide-react";
import PageHero from "@/components/PageHero";
import Marquee from "@/components/Marquee";
import SectionHeading from "@/components/SectionHeading";
import { Reveal, Stagger, StaggerItem } from "@/components/motion";
import { DISCIPLINES, ORARI_CORSI, IMAGES } from "@/data/site";

/**
 * Pagina di dettaglio generica per BJJ, Grappling, Difesa personale e Ginnastica posturale.
 * Struttura coerente col template di Lotta.jsx e Pesistica.jsx (hero + storia + benefici + orari + coaches + galleria).
 */
export default function DisciplineDetail({ slug, orariId }) {
  const d = DISCIPLINES[slug];
  const orario = ORARI_CORSI.find((o) => o.id === (orariId || slug));
  const gallery = (d.galleryKeys || []).map((k) => IMAGES[k]).filter(Boolean);

  return (
    <div data-testid={`${slug}-page`}>
      <PageHero
        image={d.hero}
        overline={d.overline}
        titleLines={d.titleLines}
        subtitle={d.intro}
      />

      {/* Storia della disciplina */}
      <section className="bg-white py-20 md:py-32" data-testid={`${slug}-storia`}>
        <div className="mx-auto grid max-w-[1400px] items-start gap-12 px-5 md:px-8 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-28">
              <SectionHeading overline={d.story.eyebrow} title={d.story.title} />
              <div className="mt-8 overflow-hidden rounded-2xl">
                <img
                  src={d.story.image}
                  alt={d.titleLines.join(" ")}
                  loading="lazy"
                  decoding="async"
                  className="aspect-[4/3] w-full object-cover"
                />
              </div>
            </div>
          </div>
          <div className="lg:col-span-7 lg:pt-16">
            <div className="space-y-6 text-base leading-relaxed text-muted-foreground md:text-lg">
              {d.story.paragraphs.map((p, i) => (
                <Reveal key={i} delay={i * 0.05}>
                  <p>{p}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="bg-cream py-20 md:py-32" data-testid={`${slug}-benefici`}>
        <div className="mx-auto max-w-[1400px] px-5 md:px-8">
          <SectionHeading
            overline="Perché praticarla"
            title="I benefici della disciplina"
            subtitle="Un percorso completo pensato per corpo e mente, con attenzione alla tecnica e alla sicurezza."
          />
          <Stagger className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {d.benefits.map((b, i) => (
              <StaggerItem key={b.title}>
                <div className="h-full rounded-2xl border border-ink/10 bg-white p-7">
                  <span className="font-display text-5xl font-extrabold tracking-tighter text-cab/25">
                    0{i + 1}
                  </span>
                  <h3 className="mt-3 font-display text-xl font-extrabold uppercase tracking-tight text-ink">
                    {b.title}
                  </h3>
                  <p className="mt-2 text-[15px] leading-relaxed text-muted-foreground">
                    {b.text}
                  </p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* Schedule + coaches */}
      <section className="bg-white py-20 md:py-32" data-testid={`${slug}-orari`}>
        <div className="mx-auto grid max-w-[1400px] gap-12 px-5 md:px-8 lg:grid-cols-2 lg:gap-16">
          <div>
            <SectionHeading
              overline="Turni di allenamento"
              title="Orari dei corsi"
              subtitle={orario?.note}
            />
            <div className="mt-10 overflow-hidden rounded-2xl border border-ink/10">
              {(orario?.slots || []).map((s, i) => (
                <Reveal key={`${s.group}-${i}`} delay={i * 0.05}>
                  <div className="flex flex-wrap items-center justify-between gap-4 border-b border-ink/10 bg-cream px-6 py-5 last:border-0">
                    <div>
                      <p className="font-display text-lg font-bold text-ink">{s.group}</p>
                      <p className="text-sm text-muted-foreground">{s.days}</p>
                    </div>
                    <span className="inline-flex items-center gap-2 rounded-md bg-cab/10 px-4 py-2 font-bold text-cab">
                      <Clock className="h-4 w-4" />
                      {s.time}
                    </span>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          <div>
            <SectionHeading overline="Il team tecnico" title="Gli istruttori" />
            <div className="mt-10 space-y-4">
              {d.coaches.map((c, i) => (
                <Reveal key={c.name} delay={i * 0.05}>
                  <div className="flex items-center gap-4 rounded-2xl border border-ink/10 bg-cream p-5">
                    <span className="inline-flex h-14 w-14 items-center justify-center rounded-xl bg-cab text-cream">
                      <Users className="h-6 w-6" />
                    </span>
                    <div>
                      <p className="font-display text-lg font-bold text-ink">{c.name}</p>
                      <p className="text-sm text-cab">{c.role}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.15}>
              <div className="mt-6 rounded-2xl bg-cab-deep p-7 text-cream">
                <div className="flex items-start gap-3">
                  <Check className="mt-1 h-5 w-5 shrink-0 text-cab-light" />
                  <p className="text-[15px] leading-relaxed text-cream/85">
                    Prima lezione di prova <strong className="text-cream">gratuita</strong> per
                    tutti i nuovi iscritti. Porta abbigliamento comodo, al resto pensiamo noi.
                  </p>
                </div>
                <Link
                  to="/dove-siamo"
                  data-testid={`${slug}-cta`}
                  className="group mt-5 inline-flex items-center gap-2 rounded-md bg-cream px-6 py-3 font-bold text-cab-deep transition-transform duration-300 hover:-translate-y-0.5"
                >
                  Prenota una prova
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {d.marquee?.length ? (
        <div className="border-y border-ink/10 bg-cream py-6">
          <Marquee items={d.marquee} outline />
        </div>
      ) : null}

      {/* Gallery */}
      {gallery.length > 0 && (
        <section className="bg-cream py-20 md:py-32" data-testid={`${slug}-gallery`}>
          <div className="mx-auto max-w-[1400px] px-5 md:px-8">
            <SectionHeading overline="In palestra" title="La galleria" />
            <div className="mt-14 columns-2 gap-4 md:columns-4 [&>*]:mb-4">
              {gallery.map((img, i) => (
                <Reveal key={i} delay={(i % 4) * 0.06}>
                  <div className="group break-inside-avoid overflow-hidden rounded-xl bg-cab-deep">
                    <img
                      src={img}
                      alt={`${d.titleLines.join(" ")} ${i + 1}`}
                      loading="lazy"
                      decoding="async"
                      className="w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
